import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootReducer } from "src/redux/reducers";
import useSkin from "src/hooks/useSkin";

// components
import { RangeInput } from "src/components";

// constants
import { BOOK_LEXILE_MAX_LEVEL, BOOK_READING_MAX_LEVEL } from "src/constants";
import {
  isHiddenGriffinCategory,
  isHiddenGriffinCefr,
  normalizeLibraryTagKey
} from "src/helpers/griffinContentFilters";

// assets
import crossIcon from "src/assets/images/crose-icon.svg";

// category icons (Griffle skin)
import catFairytale from "src/modules/griffle/assets/icons/library/cat-fairytale.svg";
import catLove from "src/modules/griffle/assets/icons/library/cat-love.svg";
import catSport from "src/modules/griffle/assets/icons/library/cat-sport.svg";
import catScifi from "src/modules/griffle/assets/icons/library/cat-scifi.svg";
import catHistory from "src/modules/griffle/assets/icons/library/cat-history.svg";
import catMystery from "src/modules/griffle/assets/icons/library/cat-mystery.svg";
import catNonfiction from "src/modules/griffle/assets/icons/library/cat-nonfiction.svg";
import catAdventure from "src/modules/griffle/assets/icons/library/cat-adventure.svg";
import catHumor from "src/modules/griffle/assets/icons/library/cat-humor.svg";
import catAnimals from "src/modules/griffle/assets/icons/library/cat-animals.svg";
import catPoetry from "src/modules/griffle/assets/icons/library/cat-poetry.svg";
import catHorror from "src/modules/griffle/assets/icons/library/cat-horror.svg";
import catBiography from "src/modules/griffle/assets/icons/library/cat-biography.svg";
import catFantasy from "src/modules/griffle/assets/icons/library/cat-fantasy.svg";
import catNature from "src/modules/griffle/assets/icons/library/cat-nature.svg";
import catScary from "src/modules/griffle/assets/icons/library/cat-scary.svg";
import catFamily from "src/modules/griffle/assets/icons/library/cat-family.svg";
import catCreative from "src/modules/griffle/assets/icons/library/cat-creative.svg";
import catSuperheroes from "src/modules/griffle/assets/icons/library/cat-superheroes.svg";
import catFunny from "src/modules/griffle/assets/icons/library/cat-funny.svg";
import catMusic from "src/modules/griffle/assets/icons/library/cat-music.svg";
import catFriends from "src/modules/griffle/assets/icons/library/cat-friends.svg";
import catBody from "src/modules/griffle/assets/icons/library/cat-body.svg";
import catFindWaldo from "src/modules/griffle/assets/icons/library/cat-find-waldo.svg";
import catPopularCharacters from "src/modules/griffle/assets/icons/library/cat-popular-characters.svg";

const CAT_ICON_MAP: Record<string, string> = {
  fairytale: catFairytale,
  fairytale_collection: catFairytale,
  "fairy tale": catFairytale,
  love: catLove,
  love_collection: catLove,
  sport: catSport,
  sport_collection: catSport,
  sports: catSport,
  "sci-fi": catScifi,
  scifi_collection: catScifi,
  "science fiction": catScifi,
  history: catHistory,
  history_collection: catHistory,
  mystery: catMystery,
  mystery_collection: catMystery,
  mysteries: catMystery,
  detective: catMystery,
  nonfiction: catNonfiction,
  nonfiction_collection: catNonfiction,
  "non-fiction": catNonfiction,
  nonfiction_collection_da: catNonfiction,
  adventure: catAdventure,
  adventure_collection: catAdventure,
  humor: catHumor,
  comedy: catHumor,
  humor_collection: catHumor,
  animals: catAnimals,
  animal: catAnimals,
  animals_collection: catAnimals,
  animal_collection: catAnimals,
  nature: catNature,
  nature_collection: catNature,
  poetry: catPoetry,
  poems: catPoetry,
  poetry_collection: catPoetry,
  horror: catHorror,
  thriller: catHorror,
  horror_collection: catHorror,
  biography: catBiography,
  biographies: catBiography,
  biography_collection: catBiography,
  fantasy: catFantasy,
  fantasy_collection: catFantasy,
  scary: catScary,
  scary_collection: catScary,
  spooky: catScary,
  family: catFamily,
  family_collection: catFamily,
  creative: catCreative,
  creative_collection: catCreative,
  superheroes: catSuperheroes,
  superhero: catSuperheroes,
  superheroes_collection: catSuperheroes,
  funny: catFunny,
  humorous: catFunny,
  funny_collection: catFunny,
  music: catMusic,
  music_collection: catMusic,
  friends: catFriends,
  friends_collection: catFriends,
  body: catBody,
  body_collection: catBody,
  "find waldo": catFindWaldo,
  find_waldo: catFindWaldo,
  waldo: catFindWaldo,
  "popular characters": catPopularCharacters,
  popular_characters: catPopularCharacters,
  popular: catPopularCharacters
};

interface IProps {
  categoryId?: number[];
  cefrId?: number[];
  topicsId?: number[];
  readingLevelValue?: number[];
  lexileLevelValue?: number[];
  handleSubmit: (
    categories: number[],
    cefr: number[],
    topics: number[],
    readingLevel: number[],
    lexileLevel: number[]
  ) => void;
  useCategories?: boolean;
  modalIdToOpen?: string;
  setOffset: Dispatch<SetStateAction<number>>;
}

const FilterModal: FC<IProps> = ({
  handleSubmit,
  categoryId,
  cefrId,
  topicsId,
  readingLevelValue,
  lexileLevelValue,
  modalIdToOpen,
  useCategories = true,
  setOffset
}) => {
  const [t] = useTranslation();
  const { isGriffle } = useSkin();

  const {
    book: { categoryList, cefrList, topicList }
  }: RootReducer = useSelector<RootReducer>((state) => state) as RootReducer;

  const [categories, setCategories] = useState<number[]>(categoryId || []);
  const [cefr, setCefr] = useState<number[]>(cefrId || []);
  const [topics, setTopics] = useState<number[]>(topicsId || []);
  const [readingLevel, setReadingLevel] = useState<number[]>(readingLevelValue || [0, BOOK_READING_MAX_LEVEL]);
  const [lexileLevel, setLexileLevel] = useState<number[]>(lexileLevelValue || [0, BOOK_LEXILE_MAX_LEVEL]);
  const [filterIsApplied, setFilterIsApplied] = useState<boolean>(false);

  const visibleCategoryList = useMemo(() => {
    if (!categoryList) return [];
    const named = categoryList.filter((item) => Boolean(item?.name));
    if (!isGriffle) return named;
    return named.filter((item) => !isHiddenGriffinCategory(item.name));
  }, [categoryList, isGriffle]);

  const visibleCefrList = useMemo(() => {
    if (!cefrList) return [];
    const named = cefrList.filter((item) => Boolean(item?.name));
    if (!isGriffle) return named;
    return named.filter((item) => !isHiddenGriffinCefr(item.name));
  }, [cefrList, isGriffle]);

  useEffect(() => {
    if (
      categories.length > 0 ||
      cefr.length > 0 ||
      topics.length > 0 ||
      readingLevel[0] > 0 ||
      readingLevel[1] < BOOK_READING_MAX_LEVEL ||
      lexileLevel[0] > 0 ||
      lexileLevel[1] < BOOK_LEXILE_MAX_LEVEL
    ) {
      setFilterIsApplied(true);
    } else {
      setFilterIsApplied(false);
    }
  }, [categories, cefr, topics, readingLevel, lexileLevel]);

  const onChange = useCallback(
    (value: number, setFunction: Dispatch<SetStateAction<number[]>>) => {
      setFunction((prev) => {
        const newItems = [...prev];
        const itemIndex = newItems.findIndex((item) => item === value);

        if (itemIndex >= 0) {
          newItems.splice(itemIndex, 1);
        } else {
          newItems.push(value);
        }
        return newItems;
      });
    },
    [setCategories]
  );

  const handleFiltersReset = () => {
    setCategories([]);
    setLexileLevel([0, BOOK_LEXILE_MAX_LEVEL]);
    setReadingLevel([0, BOOK_READING_MAX_LEVEL]);
    setCefr([]);
    setTopics([]);
    setFilterIsApplied(false);
    setOffset(0);
  };

  const handleClose = () => {
    setCategories(categoryId || categories);
    setLexileLevel(lexileLevelValue || lexileLevel);
    setReadingLevel(readingLevelValue || readingLevel);
  };

  return (
    <div className="filter-modal-sec">
      <div
        className="modal fade"
        id="exampleModal"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header modal_bg">
              <h5 className="modal-title mb-0" id="exampleModalLabel">
                {t("library_filter_modal_title")}
              </h5>
              <div className="btn-group">
                {filterIsApplied && (
                  <button type="button" className="btn btn-link" onClick={handleFiltersReset}>
                    {t("library_filter_modal_button_reset")}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close custom-modal-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  data-bs-toggle={modalIdToOpen && "modal"}
                  data-bs-target={modalIdToOpen}
                  onClick={handleClose}
                >
                  <img src={crossIcon} alt="icon" />
                </button>
              </div>
            </div>
            <div className="modal-body">
              <div className="student-edit-form m-0">
                <form>
                  {useCategories && visibleCategoryList.length > 0 && (
                    <div className="form-group">
                      <span className="label-text text-start">{t("library_filter_modal_label_select_category")}</span>
                      <ul className="d-flex flex-wrap gap-2">
                        {visibleCategoryList.map((item) => {
                          const isActive = categories ? categories.indexOf(item.id) !== -1 : null;
                          const catKey = normalizeLibraryTagKey(item.name);
                          const catIcon = isGriffle ? CAT_ICON_MAP[catKey] : undefined;

                          return (
                            <li key={item.id}>
                              <button
                                type="button"
                                className={`btn btn-outline-primary text-capitalize ${isActive ? "active" : ""}`}
                                onClick={() => {
                                  onChange(item.id, setCategories);
                                }}
                              >
                                {catIcon && <img src={catIcon} alt="" className="cat-icon" />}
                                {t(item.name)}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {visibleCefrList.length > 0 && (
                    <div className="form-group">
                      <span className="label-text text-start">{t("CEFR")}</span>
                      <ul className="d-flex flex-wrap gap-2">
                        {visibleCefrList.map((item) => {
                          const isActive = cefr ? cefr.indexOf(item.id) !== -1 : null;

                          return (
                            <li key={item.id}>
                              <button
                                type="button"
                                className={`btn btn-outline-primary text-capitalize ${isActive ? "active" : ""}`}
                                onClick={() => {
                                  onChange(item.id, setCefr);
                                }}
                              >
                                {t(item.name)}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  <div className="form-group">
                    <span className="label-text text-start">{t("Topic")}</span>
                    <ul className="d-flex flex-wrap gap-2">
                      {topicList?.map((item) => {
                        const isActive = topics ? topics.indexOf(item.id) !== -1 : null;

                        return (
                          <li key={item.id}>
                            <button
                              type="button"
                              className={`btn btn-outline-primary text-capitalize ${isActive ? "active" : ""}`}
                              onClick={() => {
                                onChange(item.id, setTopics);
                              }}
                            >
                              {t(item.name)}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="form-group">
                    <label className="label-text text-start">
                      {`${t("library_filter_modal_label_reading_level")} (${readingLevel[0]} - ${readingLevel[1]})`}
                    </label>
                    <RangeInput
                      min={0}
                      max={BOOK_READING_MAX_LEVEL}
                      step={1}
                      onChange={(values: number[]) => {
                        setReadingLevel(values);
                      }}
                      values={readingLevel}
                    />
                  </div>
                  <div className="form-group m-0">
                    <label className="label-text text-start">
                      {`${t("library_filter_modal_label_lexile_level")} (${lexileLevel[0]} - ${lexileLevel[1]})`}
                    </label>
                    <RangeInput
                      min={0}
                      max={BOOK_LEXILE_MAX_LEVEL}
                      step={1}
                      onChange={(values: number[]) => {
                        setLexileLevel(values);
                      }}
                      values={lexileLevel}
                    />
                  </div>
                </form>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                data-bs-dismiss="modal"
                onClick={handleClose}
                data-bs-toggle={modalIdToOpen && "modal"}
                data-bs-target={modalIdToOpen}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                data-bs-dismiss="modal"
                onClick={() => {
                  handleSubmit(categories, cefr, topics, readingLevel, lexileLevel);
                }}
                data-bs-target={modalIdToOpen}
                data-bs-toggle={modalIdToOpen && "modal"}
              >
                Submit
                <div className="button-hover">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 375 70"
                    width="375"
                    height="70"
                    preserveAspectRatio="xMidYMid meet"
                    style={{ width: "100%", height: "100%", transform: "translate3d(0px, 0px, 0px)" }}
                  >
                    <defs>
                      <clipPath id="__lottie_element_159">
                        <rect width="375" height="70" x="0" y="0"></rect>
                      </clipPath>
                    </defs>
                    <g clipPath="url(#__lottie_element_159)">
                      <g transform="matrix(1,0,0,1,362.5,54.75)" opacity="1" style={{ display: "block" }}>
                        <g opacity="1" transform="matrix(1,0,0,1,0,0)">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fillOpacity="0"
                            stroke="#CB1E5C"
                            strokeOpacity="1"
                            strokeWidth="4"
                            d=" M-5,-5.25 C-5,-5.25 5,5.25 5,5.25"
                          ></path>
                        </g>
                      </g>
                      <g transform="matrix(1,0,0,1,363,31.5)" opacity="1" style={{ display: "block" }}>
                        <g opacity="1" transform="matrix(1,0,0,1,0,0)">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fillOpacity="0"
                            stroke="#CB1E5C"
                            strokeOpacity="1"
                            strokeWidth="4"
                            d=" M-5.5,-0.5 C-5.5,-0.5 5.5,0.5 5.5,0.5"
                          ></path>
                        </g>
                      </g>
                      <g transform="matrix(1,0,0,1,363.25,9.25)" opacity="1" style={{ display: "block" }}>
                        <g opacity="1" transform="matrix(1,0,0,1,0,0)">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fillOpacity="0"
                            stroke="#CB1E5C"
                            strokeOpacity="1"
                            strokeWidth="4"
                            d=" M-5.25,3.25 C-5.25,3.25 5.25,-3.25 5.25,-3.25"
                          ></path>
                        </g>
                      </g>
                      <g transform="matrix(1,0,0,1,12.5,58.75)" opacity="1" style={{ display: "block" }}>
                        <g opacity="1" transform="matrix(1,0,0,1,0,0)">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fillOpacity="0"
                            stroke="#CB1E5C"
                            strokeOpacity="1"
                            strokeWidth="4"
                            d=" M5,-5.25 C5,-5.25 -5,5.25 -5,5.25"
                          ></path>
                        </g>
                      </g>
                      <g transform="matrix(1,0,0,1,12,35.5)" opacity="1" style={{ display: "block" }}>
                        <g opacity="1" transform="matrix(1,0,0,1,0,0)">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fillOpacity="0"
                            stroke="#CB1E5C"
                            strokeOpacity="1"
                            strokeWidth="4"
                            d=" M5.5,-0.5 C5.5,-0.5 -5.5,0.5 -5.5,0.5"
                          ></path>
                        </g>
                      </g>
                      <g transform="matrix(1,0,0,1,11.5,13.25)" opacity="1" style={{ display: "block" }}>
                        <g opacity="1" transform="matrix(1,0,0,1,0,0)">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fillOpacity="0"
                            stroke="#CB1E5C"
                            strokeOpacity="1"
                            strokeWidth="4"
                            d=" M5.5,3.25 C5.5,3.25 -5.5,-3.25 -5.5,-3.25"
                          ></path>
                        </g>
                      </g>
                    </g>
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
